/*
 * User-facing language for the TToria Control Centre.
 *
 * Internal values are deliberately kept unchanged in APIs, storage, audit
 * evidence, routes and action payloads. These helpers translate only at the
 * presentation boundary so the interface stays understandable without
 * weakening any platform contract.
 */

function clean(value = '', fallback = '') {
  const text = String(value ?? '').trim();
  return text || fallback;
}

function code(value = '') {
  return clean(value)
    .replace(/([a-z0-9])([A-Z])/gu, '$1_$2')
    .toLowerCase()
    .replace(/[\s.-]+/gu, '_');
}

const STATUS_LABELS = Object.freeze({
  general: Object.freeze({
    active: 'Active',
    accepted: 'Accepted',
    available: 'Available',
    blocked: 'Blocked',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    closed: 'Closed',
    complete: 'Finished',
    completed: 'Finished',
    current: 'Up to date',
    degraded: 'Some problems',
    disabled: 'Off',
    enabled: 'On',
    error: 'Needs attention',
    expired: 'Expired',
    failed: 'Did not finish',
    healthy: 'Working',
    identified: 'Cause identified',
    inactive: 'Inactive',
    in_progress: 'In progress',
    not_configured: 'Not set up',
    not_reported: 'Not available',
    not_run: 'Not checked yet',
    no_open_provider_alerts: 'No open alerts',
    ok: 'Working',
    open: 'Open',
    partial: 'Partly finished',
    passed: 'Working',
    pending: 'Waiting',
    queued: 'Waiting to start',
    read_only: 'View only',
    reported: 'Information received',
    resolved: 'Resolved',
    running: 'In progress',
    skipped: 'Not checked',
    shadow: 'Measuring only',
    shadow_only: 'Measuring only',
    enforced: 'Limit is active',
    stale: 'Out of date',
    success: 'Finished',
    successful: 'Finished',
    succeeded: 'Finished',
    unknown: 'Not available',
    unverified: 'Not confirmed',
    verified: 'Confirmed',
    warning: 'Needs attention',
    waiting: 'Waiting',
  }),
  access: Object.freeze({
    full: 'Full access',
    grace: 'Temporary access',
    read_only: 'View only',
    suspended: 'Suspended',
    security_quarantine: 'Locked for security review',
    closed: 'Closed',
  }),
  lifecycle: Object.freeze({
    draft: 'Not started',
    provisioning: 'Being set up',
    onboarding: 'Waiting for tutor',
    active: 'Open',
    closure_pending: 'Scheduled to close',
    closed: 'Closed',
    retained: 'Closed; information kept',
    purged: 'Permanently deleted',
  }),
  subscription: Object.freeze({
    active: 'Paid and active',
    trialing: 'Free trial',
    past_due: 'Payment overdue',
    unpaid: 'Payment required',
    grace: 'Temporary billing grace',
    cancel_at_period_end: 'Ends after the paid period',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    complimentary: 'Complimentary access',
    none: 'No subscription',
  }),
  invitation: Object.freeze({
    accepted: 'Accepted',
    bounced: 'Could not be delivered',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    delivered: 'Delivered',
    expired: 'Expired',
    failed: 'Could not be sent',
    pending: 'Waiting for tutor',
    provider_accepted: 'Sent',
    queued: 'Waiting to send',
    sent: 'Sent',
    waiting: 'Waiting for tutor',
  }),
  operation: Object.freeze({
    accepted: 'Accepted',
    acknowledged: 'Confirmed by the service',
    applied: 'Applied',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    complete: 'Finished',
    completed: 'Finished',
    failed: 'Did not finish',
    in_progress: 'In progress',
    partial: 'Partly finished',
    pending: 'Waiting',
    queued: 'Waiting to start',
    retrying: 'Trying again',
    running: 'In progress',
    succeeded: 'Finished',
  }),
  incident: Object.freeze({
    investigating: 'Being investigated',
    monitoring: 'Being watched',
    open: 'Open',
    resolved: 'Resolved',
  }),
  severity: Object.freeze({
    critical: 'Urgent',
    high: 'High impact',
    warning: 'Warning',
    medium: 'Medium impact',
    low: 'Low impact',
    info: 'Information',
  }),
  confidence: Object.freeze({
    high: 'Strong estimate',
    medium: 'Reasonable estimate',
    low: 'Rough estimate',
    unknown: 'Estimate quality unavailable',
  }),
});

export function plainStatus(value = '', context = 'general', fallback = 'Not available') {
  const normalized = code(value);
  return STATUS_LABELS[context]?.[normalized]
    || STATUS_LABELS.general[normalized]
    || fallback;
}

const ALERT_TITLES = Object.freeze({
  activation_email_failed: 'A tutor invitation could not be sent',
  core_access_projection_canary_failed: 'The automatic account-access check found a difference',
  support_case_projection_incomplete: 'A tutor problem report could not be fully updated',
  tenant_inventory_reconciliation_incomplete: 'Some tutor accounts could not be fully updated',
  tenant_reconciliation_failed: 'Tutor account information could not be updated',
});

export function plainAlertTitle(value = '') {
  const text = clean(value);
  return ALERT_TITLES[code(text)]
    || (/\s/u.test(text) && !text.includes('_') ? text : 'A system check needs attention');
}

const ACTION_LABELS = Object.freeze({
  account_assign_plan: 'Changed the account plan',
  account_cancel_closure: 'Cancelled the planned account closure',
  account_cancel_invitation: 'Cancelled the invitation',
  account_close: 'Scheduled the account to close',
  account_complimentary_period: 'Added complimentary access',
  account_end_trial: 'Ended the trial',
  account_export: 'Requested an account export',
  account_extend_trial: 'Extended the trial',
  account_full: 'Changed access to full access',
  account_grace: 'Added temporary access',
  account_password_reset: 'Requested a password-reset link',
  account_quarantine: 'Locked the account for security review',
  account_read_only: 'Changed access to view only',
  account_resend_invitation: 'Sent a new invitation',
  account_restore: 'Restored account access',
  account_revoke_sessions: 'Signed the tutor out everywhere',
  account_start_trial: 'Started the trial',
  account_suspend: 'Suspended the account',
  account_close_finalize: 'Finished closing the account',
  create_tutor_account: 'Created a tutor account',
  data_retention: 'Kept account information for the restore period',
  incident_create: 'Recorded a service problem',
  manual_control_plane_reconciliation: 'Checked all tutor account information',
  publish_expired_feature_policy: 'Removed an expired feature exception',
  publish_tenant_policy: 'Changed account features',
  permanent_purge: 'Permanent deletion remains off',
  retry_operation: 'Tried a change again',
  set_tenant_student_capacity: 'Changed the account student limit',
  set_plan_student_capacity: 'Changed the plan student limit',
});

export function plainAction(value = '', operation = {}) {
  const text = clean(value);
  const normalized = code(text).replace(/^control_/u, '');
  if (normalized === 'account_password_reset') {
    if (operation.deliveryState === 'uncertain') return 'Password-reset email delivery unconfirmed';
    if (operation.state === 'completed' || operation.status === 'completed') return 'Sent a password-reset link';
  }
  if (normalized.startsWith('acknowledge_support_case')) return 'Marked a tutor problem as received';
  if (normalized.startsWith('set_plan_student_capacity')) return ACTION_LABELS.set_plan_student_capacity;
  if (ACTION_LABELS[normalized]) return ACTION_LABELS[normalized];
  return /\s/u.test(text) && !text.includes('_') ? text : 'Recorded a system change';
}

const EVENT_LABELS = Object.freeze({
  authentication_failed: 'Sign-in attempt did not work',
  authentication_succeeded: 'Signed in',
  bootstrap_completed: 'Finished the first owner setup',
  elevation_completed: 'Unlocked sensitive changes',
  elevation_failed: 'Could not unlock sensitive changes',
  logout: 'Signed out',
  passkey_registered: 'Added a passkey',
  recovery_code_used: 'Used a recovery code',
  session_revoked: 'Ended a sign-in session',
  totp_verified: 'Used an authenticator app',
});

export function plainEvent(value = '') {
  const text = clean(value);
  const normalized = code(text);
  return EVENT_LABELS[normalized] || plainAction(text);
}

const SETTING_LABELS = Object.freeze({
  backup_and_recovery: 'Backups and recovery',
  billing_enforcement: 'Automatic billing restrictions',
  break_glass_identity: 'Emergency owner sign-in',
  control_plane_mode: 'Control Centre change access',
  permanent_purge: 'Permanent deletion',
  privacy_impact_assessment: 'Privacy review',
});

const VALUE_LABELS = Object.freeze({
  enabled: 'Turned on',
  evidence_reference: 'Supporting record',
  last_backup_at: 'Last backup',
  last_restore_test_at: 'Last recovery test',
  last_tested_at: 'Last tested',
  mode: 'Current mode',
  next_test_due_at: 'Next test due',
  production_blocking: 'Must be finished before production',
  reason: 'Why',
  status: 'Status',
});

export function plainSettingLabel(value = '') {
  return SETTING_LABELS[code(value)] || 'Control Centre setting';
}

export function plainSettingValueLabel(value = '') {
  return VALUE_LABELS[code(value)] || 'Recorded information';
}

const METHOD_LABELS = Object.freeze({
  activity_weighted: 'Estimated from account activity',
  equal_share: 'Shared equally between accounts',
  direct: 'Reported directly by the connected service',
  sum: 'Added together for the selected period',
  latest: 'Most recent recorded value',
  max: 'Highest recorded value in the selected period',
});

export function plainMethod(value = '', fallback = 'How this was calculated is unavailable') {
  return METHOD_LABELS[code(value)] || fallback;
}

const SYSTEM_MESSAGES = Object.freeze({
  'No versioned tenant policy has been published yet.': 'No saved account feature settings are available yet.',
  'No staging canary evidence has been recorded yet.': 'The safe automatic test has not run yet.',
  'The staging canary has not produced evidence yet.': 'The safe automatic test has not returned a result yet.',
  'The synthetic staging projections need review.': 'The safe test account returned different access results and needs review.',
  'Tutor Desk and Student Portal reported matching core access projections.': 'Tutor Desk and Student Portal reported the same account access.',
  'This session can review information but cannot request changes.': 'You can review information, but sensitive changes are locked for this sign-in.',
  'The main sign-in and account-access journey passed.': 'The automatic sign-in and account access test worked.',
  'A separate emergency identity must be configured and tested before production.': 'A separate emergency owner sign-in must be set up and tested before production.',
  'Permanent purge remains server-disabled until its independent privacy, legal, backup and recovery certification passes.': 'Permanent deletion stays off until the privacy, legal, backup, and recovery checks are independently approved.',
});

export function plainSystemMessage(value = '', fallback = '') {
  const text = clean(value, fallback);
  return SYSTEM_MESSAGES[text] || text;
}

export function plainBoolean(value, yes = 'Yes', no = 'No') {
  return value === true ? yes : value === false ? no : 'Not available';
}
